import _ from 'lodash';
import React, { useCallback } from 'react';
import { ObjectSchema } from '@scale/shared/taskTemplate/types';
import { TypeBuilderMapType } from 'react-scale/corp/components/QM/taskTemplatesEditor/BuilderSchemasType';
import cx from 'classnames';
import { twMerge } from 'tailwind-merge';
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import { ChunkColor } from 'react-scale/dashboard.scale.com/components/SelfServe/Onboarding/Task/components/TaxonomyEditor';
import { Icon, Accordion, Checkbox, Tag } from '@scale/scaleui';
import { TextCollectionField } from '@scale/shared/types/params/textcollection';

const ObjectBuilder: React.FC<{
  title: string;
  object: any;
  schema: ObjectSchema;
  updateObject: (newObject: any) => void;
  deleteObject: () => void;
  cloneObject?: () => void;
  showBuilder: boolean;
  toggleShowBuilder: () => void;
  dragHandleProps?: DraggableProvidedDragHandleProps;
  labelColor?: ChunkColor;
  toggleLabel?: () => void;
  fullWidth?: boolean;
  tags?: Array<string | undefined>;
  restrictFields?: boolean;
  taskTemplateParams?: Record<string, any>;
  fields?: TextCollectionField[];
  showError?: boolean;
  getWarningOnModify?: (propertyName: string) => string | undefined;
  draggable?: boolean;
  canDelete?: boolean;
  isHidden?: boolean;
  typeBuilderMap: TypeBuilderMapType;
}> = ({
  title,
  object,
  schema,
  updateObject,
  deleteObject,
  cloneObject,
  showBuilder,
  toggleShowBuilder,
  dragHandleProps,
  labelColor,
  toggleLabel,
  fullWidth,
  tags,
  restrictFields,
  taskTemplateParams,
  fields,
  showError,
  getWarningOnModify,
  draggable = true,
  canDelete = true,
  isHidden = false,
  typeBuilderMap,
}) => {
  const updateProperty = useCallback(
    (name: string, value: any) => {
      updateObject({ ...object, [name]: value });
    },
    [object, updateObject],
  );
  const baseProperties = _.pickBy(schema, i => !i.hidden && !i.deprecated);
  const advancedProperties = _.pickBy(schema, i => !!i.hidden && !i.deprecated);

  const propertyToBuilder = useCallback(
    (key: string) => {
      const props = _.omitBy(
        {
          required: schema[key].required,
          hint: schema[key].hint,
          step: schema[key].step,
          min: schema[key].min,
          max: schema[key].max,
          useMarkdownEditor: schema[key].useMarkdownEditor,
          allowMultiple: schema[key].allowMultiple,
          choices: schema[key].choices,
          taskTemplateParams,
          fields,
        },
        _.isUndefined,
      );
      const { inputType } = schema[key];
      const BuilderComponent = typeBuilderMap[inputType];
      if (!BuilderComponent) {
        throw new Error(`BuilderComponent not found for inputType: ${inputType}`);
      }
      return (
        <div key={key} className="mb-2">
          <BuilderComponent
            {...props}
            name={schema[key].displayName}
            typeBuilderMap={typeBuilderMap}
            existingValue={object[key]}
            updateProperty={(value: any) => updateProperty(key, value)}
            readOnly={restrictFields}
            disabled={restrictFields}
            warningOnModify={getWarningOnModify && getWarningOnModify(schema[key].displayName)}
          />
        </div>
      );
    },
    [schema, object, updateProperty, restrictFields, taskTemplateParams],
  );
  const className = isHidden ? 'bg-neutral-100' : '';

  return (
    <div
      className={cx(
        fullWidth ? 'max-w-full' : 'max-w-sm',
        'pb-2 mb-2 flex flex-row items-center justify-between',
      )}
    >
      {draggable && (
        <div {...dragHandleProps} className="flex-shrink-0">
          <Icon variant="neutral" size="md" icon="grip-vertical" />
        </div>
      )}
      <Accordion
        className={twMerge('flex-1 min-w-0', className)}
        expanded={showBuilder}
        onChange={toggleShowBuilder}
        header={
          <div className="flex justify-between items-center w-full">
            <div className="flex justify-between items-center gap-2">
              {showError && (
                <Icon variant="danger" size="sm" icon="exclamation-circle" className="mr-2" />
              )}
              <div className="flex py-1">
                {toggleLabel && (
                  <div className="mr-2" onClick={e => e.stopPropagation()}>
                    <Checkbox
                      id={title}
                      checked={!!labelColor}
                      color={labelColor}
                      onChange={toggleLabel}
                      label=""
                    />
                  </div>
                )}
                {isHidden && (
                  <Icon
                    tooltip={
                      'Field is not visible to customers. To make it visible, make sure this field is checked on the "Customer Visibility" tab.'
                    }
                    icon="eye-slash"
                    size="sm"
                  />
                )}
                {title}
              </div>
              {tags &&
                tags.map(tag => {
                  if (!tag) return null;
                  return (
                    <Tag variant="info" size="sm" key={`${tag}-tag`}>
                      {tag}
                    </Tag>
                  );
                })}
            </div>
            <div className="flex" onClick={e => e.stopPropagation()}>
              {cloneObject && !restrictFields && (
                <div className="mr-2">
                  <Icon
                    hasBackground
                    variant="info"
                    size="sm"
                    icon="clone"
                    tooltip="Duplicate"
                    onClick={() => cloneObject()}
                  />
                </div>
              )}
              {canDelete && (
                <div className="mr-2">
                  <Icon
                    hasBackground
                    size="sm"
                    icon="trash"
                    tooltip="Remove"
                    variant="danger"
                    onClick={() => deleteObject()}
                  />
                </div>
              )}
            </div>
          </div>
        }
        target={
          <>
            {Object.keys(baseProperties).map(propertyToBuilder)}
            {_.keys(advancedProperties).length > 0 && (
              <>
                <div className="pb-2">
                  <Accordion
                    className="flex-1"
                    header="Advanced Properties"
                    target={<>{Object.keys(advancedProperties).map(propertyToBuilder)}</>}
                  />
                </div>
              </>
            )}
          </>
        }
      />
    </div>
  );
};

export default ObjectBuilder;
